const quartile = (arr, q) => {
    const pos = (arr.length - 1) * q;
    const base = Math.floor(pos);
    const rest = pos - base;

    if (arr[base + 1] !== undefined) {
        return arr[base] + rest * (arr[base + 1] - arr[base]);
    }

    return arr[base];
};

const buildBoxData = (values) => {
    const sorted = [...values].sort((a, b) => a - b);

    return [
        sorted[0],
        quartile(sorted, 0.25),
        quartile(sorted, 0.5),
        quartile(sorted, 0.75),
        sorted[sorted.length - 1]
    ];
};

const buildOutliers = (values, index) => {
    const sorted = [...values].sort((a, b) => a - b);

    const q1 = quartile(sorted, 0.25);
    const q3 = quartile(sorted, 0.75);
    const iqr = q3 - q1;

    const lower = q1 - iqr * 1.5;
    const upper = q3 + iqr * 1.5;

    return sorted
        .filter(v => v < lower || v > upper)
        .map(v => [index, v]);
};

export const buildBoxPlotData = (rawData) => {
    const grouped = {};

    rawData.forEach(item => {
        const day = item.dateTimeLabel.substring(0, 10);

        if (!grouped[day]) {
            grouped[day] = {
                shaft: [],
                horizon: [],
                vertical: []
            };
        }

        grouped[day].shaft.push(item.overallShaft);
        grouped[day].horizon.push(item.overallHorizon);
        grouped[day].vertical.push(item.overallVertical);
    });

    const dates = Object.keys(grouped).sort();

    return {
        x: dates,

        shaft: dates.map(date =>
            buildBoxData(grouped[date].shaft)
        ),

        horizon: dates.map(date =>
            buildBoxData(grouped[date].horizon)
        ),

        vertical: dates.map(date =>
            buildBoxData(grouped[date].vertical)
        ),

        shaftOutliers: dates.flatMap((date, index) =>
            buildOutliers(grouped[date].shaft, index)
        ),

        horizonOutliers: dates.flatMap((date, index) =>
            buildOutliers(grouped[date].horizon, index)
        ),

        verticalOutliers: dates.flatMap((date, index) =>
            buildOutliers(grouped[date].vertical, index)
        ),
    };
};

export const buildVibrationData = (list) => ({
    x: list.map(v => v.dateTimeLabel),
    xs: list.map(v => Number(v.overallShaft.toFixed(2))),
    ys: list.map(v => Number(v.overallHorizon.toFixed(2))),
    zs: list.map(v => Number(v.overallVertical.toFixed(2))),
});

export const buildNoiseData = (list) => ({
    x: list.map(v => v.ymd),
    rms: list.map(v => Number(v.rms.toFixed(2))),
    skew: list.map(v => Number(v.skew.toFixed(2))),
    kurt: list.map(v => Number(v.kurtosis.toFixed(2))),
});

export const buildTemperatureData = (list) => ({
    x: list.map(v => v.ymd),
    temps: list.map(v => Number(v.temperature.toFixed(2))),
});

export const isSameData = (prev, next) => {
  if (!prev) return false;
  return (
      prev.x.length === next.x.length &&
      prev.x.at(-1) === next.x.at(-1)
  );
  // JSON.stringify(prev) === JSON.stringify(next);
}

export const isSameGraphData = (prev, next, chartType) => {
  // 1. prev나 next 객체가 둘 중 하나라도 없으면 다른 데이터로 취급
  if (!prev || !next) return false;

  // 2. 진동 (AVG / BOX)
  if (chartType === "AVG" || chartType === "BOX") {
      return (
          prev.averages?.length === next.averages?.length &&
          prev.rawDetails?.length === next.rawDetails?.length &&
          prev.averages?.[0]?.ymd === next.averages?.[0]?.ymd &&
          prev.rawDetails?.[0]?.dgnssAt === next.rawDetails?.[0]?.dgnssAt
      );
  }

  // 3. 기존 (소음, 온도 등) - 옵셔널 체이닝(?.)으로 안전하게 길이 비교
  if (!prev.x || !next.x) return false;

  return (
      prev.x.length === next.x.length &&
      prev.x.at(-1) === next.x.at(-1)
  );
};
